import React, { startTransition, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { bind, bindAll } from 'bind-event-listener';
import { useIntl } from 'react-intl-next';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import { type NodeType } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { type MentionNameDetails } from '@atlaskit/mention';
import { fg } from '@atlaskit/platform-feature-flags';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { usePublish } from '@atlaskit/rovo-triggers';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';
import { type EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import type { AIPlugin } from '../../../aiPluginType';
import { aiExperienceDecorationPluginKey } from '../../../pm-plugins/decoration/decoration-plugin-key';
import {
	useInEditorDirectStreaming,
	type StreamFns,
} from '../../../pm-plugins/in-editor-direct-streaming/hooks/useInEditorDirectStreaming';
import { type EditorPluginAIProvider, type EndExperience } from '../../../types/types';
import {
	AIExperienceCommonDataContext,
	useAIExperienceCommonDataContext,
	type AIExperienceCommonDataContextData,
} from '../../hooks/useAIExperienceCommonData';
import { useAIExperienceService } from '../../hooks/useAIExperienceService';
import { CONFIG_ITEM_KEYS } from '../../prebuilt/config-items/config-item-keys';
import type {
	AnalyticsContext,
	EditorPluginAIConfigItem,
	SelectionType,
} from '../../prebuilt/config-items/config-items';
import { createFreeGenerateEditorAgentConfigItem } from '../../prebuilt/config-items/prompts/free-generate-editor-agent/free-generate-editor-agent';
import { isAdfStreamingEnabled } from '../../utils/adf-streaming';
import type {
	InvokedFrom,
	InvokedFromTriggerMethod,
} from '../../utils/analytics/analyticsFlowTypes';
import {
	AnalyticsFlowContextProvider,
	useAnalyticsFlow,
	useCreateAnalyticsFlow,
} from '../../utils/analytics/analyticsFlowUtils';
import { rangeCharWordCount, useFireAIAnalyticsEvent } from '../../utils/analytics/utils';
import { shouldDiscardEndExperienceImmediately } from '../../utils/discard-handling';
import { FAILURE_REASON } from '../../utils/errors';
import { getPMFragmentWithFallback } from '../../utils/get-pm-fragment-with-fallback';
import { adfResponseToFragment } from '../../utils/request/adf-response';
import { getLoadingMessage } from '../../utils/request/get-loading-message';
import { useStreamFlowPacerRef } from '../../utils/request/stream-flow-pacer';
import { getSelectionCategory } from '../../utils/selection';
import { createStreaming, type StreamingHandler } from '../../utils/streaming';
import type {
	AIExperienceMachineContext,
	AIExperienceMachineEvent,
	AIExperienceMachineState,
	Positions,
} from '../../utils/xstate/get-ai-experience-service';
import { ModalRegionErrorBoundary } from '../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { UFOLoadingStatusTracker } from '../UFOLoadingStatusTracker/UFOLoadingStatusTracker';

import { AcceptRejectScreenWithLogic } from './screens-with-logic/AcceptRejectScreenWithLogic';
import { DiscardScreenWithLogic } from './screens-with-logic/DiscardScreenWithLogic';
import { ErrorScreenWithLogic } from './screens-with-logic/ErrorScreenWithLogic';
import { InvalidInputScreenWithLogic } from './screens-with-logic/InvalidInputScreenWithLogic';
import { LoadingScreenWithLogic } from './screens-with-logic/LoadingScreenWithLogic';
import { NoImprovementScreenWithLogic } from './screens-with-logic/NoImprovementScreenWithLogic';
import { PreviewScreenWithLogic } from './screens-with-logic/PreviewScreenWithLogic';
import { PromptScreenWithLogic } from './screens-with-logic/PromptScreenWithLogic';
import { UserInputCommandPaletteWithLogic } from './screens-with-logic/UserInputCommandPaletteWithLogic';

type ExperienceApplicationProps = {
	aiGroupId?: string;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	appearance: EditorAppearance;
	canInlineStreamChanges: () => boolean;
	configItem: EditorPluginAIConfigItem;
	context: AIExperienceMachineContext;
	defaultAgent?: EditorAgent;
	editorPluginAIProvider: EditorPluginAIProvider;
	// all props under here may change based on how we wire things together
	editorView: EditorView;
	/**
	 * Called to end the experience
	 *
	 * Note: the ExperienceApplication will do additional cleanup work after calling this in
	 * its react unmount cleanup.
	 */
	endExperience: EndExperience;
	/** Engagement platform plugin API for starting and stopping messages. */
	engagementPlatformApi?: AIExperienceCommonDataContextData['engagementPlatformApi'];
	getMentionNameDetails?: (id: string) => Promise<MentionNameDetails | undefined>;
	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	/**
	 * Initial positions of the user's selection
	 */
	initialPositions?: Positions;
	initialPrompt?: string;
	interactionSessionId?: string;
	lastTriggeredFrom?: InvokedFrom;

	/**
	 * Confidence score will be used passed in analytics events.
	 * It is currently only set when "Improve writing" is triggered from the
	 * 	"Improve writing" nudge.
	 */
	nudgeMetrics?: Record<string, number | undefined>;
	/**
	 * For empty selections, the start and end will be the same
	 */
	positions: Positions;
	providerFactory: ProviderFactory;
	// Context by `useAIExperienceService` which creates the xstate machine and passes here
	send: (event: AIExperienceMachineEvent) => void;
	state: AIExperienceMachineState;
	triggeredFor?: NodeType;
	triggerMethod?: InvokedFromTriggerMethod;
};

type ExperienceApplicationStateManagerProps = {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	context: AIExperienceMachineContext;
	initialPrompt?: string;
	modalRef?: React.RefObject<HTMLDivElement>;
	state: AIExperienceMachineState;
	streamFlowPacer: StreamFlowPacer | null;
};

type UseAIExperienceMachineProps = {
	baseGenerate: EditorPluginAIConfigItem;
	configItem: EditorPluginAIConfigItem;
	intl: ReturnType<typeof useIntl>;
	isEmpty: boolean;
	positions: Positions;
	props: ExperienceApplicationStateWrapperProps;
	selectionType: SelectionType;
	streamingHandler: StreamingHandler | undefined;
};

const useAIExperienceMachine = conditionalHooksFactory(
	() => fg('aifc_create_enabled'),
	({
		props,
		intl,
		positions,
		isEmpty,
		selectionType,
		configItem,
		baseGenerate,
		streamingHandler,
	}: UseAIExperienceMachineProps) => {
		const { send, state, context } = props;

		// Send this synchronously
		useLayoutEffect(() => {
			send({
				type: 'initialise context',
				context: {
					analyticsContext: props.analyticsContext,
					aiProvider: props.editorPluginAIProvider,
					intl,
					editorView: props.editorView,
					positions: positions,
					isEmpty,
					selectionType,
					baseGenerate,
					configItem,
					aiLifeCycleDynamicAttributesGetter:
						props.editorPluginAIProvider?.aiLifeCycleDynamicAttributesGetter,
					markdown: '',
					latestPromptResponse: undefined,
					editCount: 0,
					refinementCount: 0,
					retryPromptCount: 0,
					latestPromptRetried: false,
					userInput: '',
					apiErrorInfo: {},
					responseHistory: { positionFromEnd: 0, entries: [], totalSize: 0, traversals: 0 },
					channelId: undefined,
					getMentionNameDetails: props.getMentionNameDetails,
					triggeredFor: props.triggeredFor,
					usePageKnowledge: true,
					endExperience: props.endExperience,
					streamingHandler,
				},
			});
			return () => {
				send({ type: 'reset' });
			};
			// This must ONLY run on mount/unmount to replicate the `useRef` behaviour of setting up the
			// machine once. If this re-runs it will reset our current state machine
			// Long term we should refactor this the react lifecycle isn't tied to xstate
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		return { context, state, send };
	},
	({
		props,
		intl,
		positions,
		isEmpty,
		selectionType,
		configItem,
		baseGenerate,
		streamingHandler,
	}: UseAIExperienceMachineProps) => {
		const { context, state, send } = useAIExperienceService({
			context: {
				analyticsContext: props.analyticsContext,
				aiProvider: props.editorPluginAIProvider,
				intl,
				editorView: props.editorView,
				positions: positions,
				isEmpty,
				selectionType,
				baseGenerate,
				configItem,
				aiLifeCycleDynamicAttributesGetter:
					props.editorPluginAIProvider?.aiLifeCycleDynamicAttributesGetter,
				markdown: '',
				latestPromptResponse: undefined,
				editCount: 0,
				refinementCount: 0,
				retryPromptCount: 0,
				latestPromptRetried: false,
				userInput: '',
				apiErrorInfo: {},
				responseHistory: { positionFromEnd: 0, entries: [], totalSize: 0, traversals: 0 },
				channelId: undefined,
				getMentionNameDetails: props.getMentionNameDetails,
				triggeredFor: props.triggeredFor,
				usePageKnowledge: true,
				endExperience: props.endExperience,
				streamingHandler,
			},
		});
		return { context, state, send };
	},
);

export function ExperienceApplicationStateManager(props: ExperienceApplicationStateManagerProps) {
	const { api } = props;
	/**
	 * Note -- because we are on an old version of xstate -- when adding new states
	 * they will not automatically be typed.
	 *
	 * You need to add these in the `AIExperienceMachineState` type in the
	 * `get-experience-service.ts` file
	 **/
	const state = props.state;
	/**
	 * Note -- because we are on an old version of xstate -- when adding new events
	 * they will not automatically be typed.
	 *
	 * You need to add these in the `AIExperienceMachineEvent` type in the
	 * `get-experience-service.ts` file
	 **/
	const context = props.context;
	const aiExperienceCommonData = useAIExperienceCommonDataContext();

	const { formatMessage } = useIntl();

	const publish = usePublish('ai-mate');

	let mountDecorations: StreamFns['mountDecorations'],
		updateStream: StreamFns['updateStream'],
		discardStream: StreamFns['discardStream'];

	const inEditorDirectStreamingHook = useInEditorDirectStreaming({
		api,
		configItem: props.context.configItem,
		isProgrammaticScroll: aiExperienceCommonData.isProgrammaticScroll,
		positions: props.context.positions,
		view: props.context.editorView,
		modalRef: props.modalRef,
		aiSessionId: aiExperienceCommonData.aiSessionId,
	});

	const isInlineStreaming = fg('aifc_create_enabled');
	const autoAcceptAIChanges = aiExperienceCommonData.canInlineStreamChanges();

	if (isInlineStreaming) {
		mountDecorations = inEditorDirectStreamingHook.mountDecorations;
		updateStream = inEditorDirectStreamingHook.updateStream;
		discardStream = inEditorDirectStreamingHook.discardStream;
	}

	React.useEffect(() => {
		const send = aiExperienceCommonData.sendToAIExperienceMachine;
		if (send && state === 'started state') {
			send({ type: 'kick off' });
		}
	}, [state, aiExperienceCommonData.sendToAIExperienceMachine]);

	/**
	 * Used to pass the backendModel to analytics and
	 * feedback submission metadata
	 */
	const backendModel = context?.configItem?.getBackendModel?.(!!context?.latestPromptResponse);

	const analyticsFlow = useAnalyticsFlow();

	React.useEffect(() => {
		analyticsFlow.updateCommonAttributes({
			backendModel,
			channelId: context.channelId,
			...analyticsFlow.getUserFlowCommonAttributes(context.configItem),
			autoAcceptAIChanges,
			inlineStreamingAvailable: fg('aifc_create_enabled'),
		});
	}, [
		analyticsFlow,
		backendModel,
		context.channelId,
		context.configItem,
		isInlineStreaming,
		autoAcceptAIChanges,
	]);

	const aiLifeCycleDynamicAttributesGetter = context.aiLifeCycleDynamicAttributesGetter;
	React.useEffect(() => {
		if (aiLifeCycleDynamicAttributesGetter) {
			analyticsFlow.registerAiLifeCycleDynamicAttributesGetter(aiLifeCycleDynamicAttributesGetter);
		}
	}, [analyticsFlow, aiLifeCycleDynamicAttributesGetter]);

	// Note -- the intention is to move the creation of the state machine higher
	// and for the ExperienceApplication (react component that appears in the modal)
	// to only be started once the machine is in the modal active state.
	if (state === 'started state') {
		return null;
	}

	const isModalActiveState = typeof state !== 'string' && state['modal active state'];

	if (!isModalActiveState) {
		throw new Error('AI machine state is not: modal active state or modal error state');
	}

	const experienceState = state['modal active state']['experience'];

	const isLastResponseTooSimilar =
		context.responseHistory.entries[context.responseHistory.entries.length - 1]?.responseTooSimilar;
	// We use the FG directly here rather then canInlineStreamChanges() because this new screen will be shown regardless if
	// the fallback modal is shown or not.
	if (isLastResponseTooSimilar && fg('aifc_create_enabled')) {
		return <NoImprovementScreenWithLogic discardStream={discardStream} />;
	}

	if (fg('platform_editor_ai_handle_invalid_input')) {
		if (
			(typeof experienceState === 'object' &&
				'error state' in experienceState &&
				experienceState['error state'] === 'active') ||
			(typeof experienceState !== 'string' && 'error state' in experienceState)
		) {
			if (context.apiErrorInfo.failureReason === FAILURE_REASON.NO_TEXTUAL_CONTENT) {
				return (
					<InvalidInputScreenWithLogic
						errorInfo={context.apiErrorInfo}
						contextStatistics={context.contextStatistics}
					/>
				);
			}
			return <ErrorScreenWithLogic errorInfo={context.apiErrorInfo} />;
		}
	} else {
		if (
			typeof experienceState === 'object' &&
			'error state' in experienceState &&
			experienceState['error state'] === 'active'
		) {
			return <ErrorScreenWithLogic errorInfo={context.apiErrorInfo} />;
		}
	}

	if (
		typeof state['modal active state'] === 'object' &&
		state['modal active state']['discard'] === 'active'
	) {
		return (
			<DiscardScreenWithLogic
				state={experienceState}
				channelId={context.channelId}
				editCount={context.editCount}
				retryPromptCount={context.retryPromptCount}
				history={context.responseHistory}
				latestPrompt={context.latestPrompt}
				onceDiscard={context.onceDiscard}
				streamFlowPacer={props.streamFlowPacer}
				discardStream={discardStream}
				api={api}
			/>
		);
	}

	if (typeof experienceState === 'string' && experienceState === 'command palette state') {
		if (fg('aifc_create_enabled')) {
			return <PromptScreenWithLogic context={context} />;
		}
		return (
			<UserInputCommandPaletteWithLogic context={context} initialPrompt={props.initialPrompt} />
		);
	}

	if (typeof experienceState === 'object' && 'loading state' in experienceState) {
		const loadingState = experienceState['loading state'];
		if (loadingState === 'loading') {
			/**
			 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
			 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
			 */
			const childShortTitle =
				context.parentPresetPromptLabel && context.configItem.nestingConfig
					? formatMessage(context.configItem.nestingConfig.shortTitle)
					: '';
			const loadingMessage = getLoadingMessage({ status: context?.loadingStatus, formatMessage });

			return (
				<>
					<LoadingScreenWithLogic
						mountDecorations={mountDecorations}
						updateStream={updateStream}
						parentPresetPromptLabel={context.parentPresetPromptLabel}
						childShortTitle={childShortTitle}
						title={
							loadingMessage
								? loadingMessage
								: formatMessage(aiExperienceApplicationMessages.loadingTitle)
						}
						markdown={context.markdown}
						adfResponse={context.adfResponse}
						adfResponseNodeWrappers={context.adfResponseNodeWrappers}
						latestPrompt={context.latestPrompt}
						promptTrigger={context.promptTrigger}
						refinementCount={context.refinementCount}
						latestPromptRetried={context.latestPromptRetried}
						idMap={context.idMap}
						contextStatistics={context.contextStatistics}
						hideLoadingScreen={aiExperienceCommonData.hideLoadingScreen}
						api={api}
					/>
					<UFOLoadingStatusTracker loadingStatus={context.loadingStatus} />
				</>
			);
		}
	}

	if (typeof experienceState !== 'string' && 'review state' in experienceState) {
		if (updateStream) {
			let pmFragment;
			if (context.adfResponse && isAdfStreamingEnabled()) {
				pmFragment = adfResponseToFragment({
					schema: context.editorView.state.schema,
					adfResponse: context.adfResponse,
					adfResponseNodeWrappers: context.adfResponseNodeWrappers,
				});
			} else {
				pmFragment = getPMFragmentWithFallback({
					markdown: context.markdown || '',
					schema: context.editorView.state.schema,
					source: 'preview',
					fireAIAnalyticsEvent: (event) => {},
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					idMap: context.idMap!,
				}).pmFragment;
			}

			if (pmFragment) {
				// TODO: EDITOR-1814 - this should be in useEffect
				updateStream?.({
					fragment: pmFragment,
					hasUserScrolled: aiExperienceCommonData.isUserScroll.current,
					isFinalStream: true,
				});
			}

			const decorationPluginState = aiExperienceDecorationPluginKey.getState(
				context.editorView.state,
			);
			// when auto accept is on, end immediately
			// when auto accept is off
			// - if it's staging first-time streaming, withAcceptReject is false, end immediately
			if (autoAcceptAIChanges || !decorationPluginState?.withAcceptReject) {
				context.endExperience();
				return null;
			} else {
				// if there is nextSessionId, it means there is an ai session running
				// - we don't show the accept reject screen
				// if there is no nextSessionId, it means the ai session ended or there is no ai session at all (such as IW)
				// - then we can show the accept reject screen
				return decorationPluginState?.nextSessionId ? null : (
					<AcceptRejectScreenWithLogic
						aiExperienceMachineContext={context}
						backendModel={backendModel}
						discardStream={discardStream}
					/>
				);
			}
		} else {
			const action = context.rovoActions ? context.rovoActions[context.selectionType] : undefined;
			return (
				<PreviewScreenWithLogic
					aiExperienceMachineContext={context}
					isInputActive={experienceState['review state'] === 'refinement'}
					backendModel={backendModel}
					rovoAction={action}
					rovoPublish={publish}
				/>
			);
		}
	}

	// When cleaning up, platform_editor_ai_handle_invalid_input, remove below if block.
	if (!fg('platform_editor_ai_handle_invalid_input')) {
		if (typeof experienceState !== 'string' && 'error state' in experienceState) {
			return <ErrorScreenWithLogic errorInfo={context.apiErrorInfo} />;
		}
	}

	throw new Error('unhandled state');
}

type ExperienceApplicationStateWrapperProps = ExperienceApplicationProps & {
	/** The analytics context for the experience */
	analyticsContext: AnalyticsContext;
};

function canReplaceBaseGenerate(aiProvider: EditorPluginAIProvider) {
	return aiProvider.isRovoEnabled && aiProvider.useFreeGenerateEditorAgent;
}

function useScrollDetection({ modalRef }: { modalRef: React.RefObject<HTMLDivElement> }) {
	const isUserScroll = useRef<boolean>(false);
	/**
	 * isProgrammaticScroll is set to true initially
	 * to detect any initial changes on render which affect
	 * scroll position. There will be multiple scroll events
	 * within a single user scroll so it will be set to false
	 * accordingly within a single scroll action.
	 */
	const isProgrammaticScroll = useRef<boolean>(true);

	useEffect(() => {
		if (!fg('aifc_create_enabled')) {
			return;
		}
		const modalEl = modalRef.current;
		if (!modalEl) {
			return;
		}
		const scrollParentEl: HTMLElement | false = findOverflowScrollParent(modalEl);
		if (!scrollParentEl) {
			return;
		}
		isUserScroll.current = false;
		const unbind = bind(scrollParentEl, {
			type: 'scroll',
			listener: (event) => {
				if (!isProgrammaticScroll.current) {
					isUserScroll.current = true;
					unbind();
				}
				isProgrammaticScroll.current = false;
			},
		});
		return () => {
			unbind();
		};
	}, [modalRef]);

	return { isUserScroll, isProgrammaticScroll };
}

function focusEditor(
	editorView: EditorView,
	{ focusEditorContentArea }: { focusEditorContentArea: boolean } = {
		focusEditorContentArea: false,
	},
) {
	if (!editorView.hasFocus()) {
		editorView.focus();
	}

	// when we disable editor while streaming, document.body will be focused
	// we need to focus editor to make pressing esc to work
	const editorContentArea = editorView?.dom.closest<HTMLElement>('.ak-editor-content-area');
	if (
		editorContentArea &&
		!editorContentArea.contains(document.activeElement) &&
		focusEditorContentArea &&
		fg('platform_editor_ai_aifc_patch_beta')
	) {
		editorContentArea.focus();
	}
}

// document.body will be focused when we switch from one screen to another
// (can be triggered by clicking button or pressing Esc key)
// which will make pressing Esc not working because we're listening Esc on editor
// so we should focus editor when it's not focused
function useFocusEditor({
	modalRef,
	editorView,
	state,
	cancelRovoPrompt,
}: {
	cancelRovoPrompt?: () => void;
	editorView: EditorView;
	modalRef: React.RefObject<HTMLDivElement>;
	state: AIExperienceMachineState;
}) {
	// state is not stable, but we want to run the effect when we switch screen, so use str as the dependency
	const stateStr = JSON.stringify(state);
	useEffect(() => {
		if (!modalRef.current) {
			return;
		}
		if (fg('aifc_create_enabled')) {
			const modalHasFocus = modalRef.current.contains(document.activeElement);
			if (!modalHasFocus && stateStr.indexOf('modal active state') !== -1) {
				focusEditor(editorView, { focusEditorContentArea: true });
			}
		}
	}, [modalRef, editorView, stateStr]);

	useEffect(() => {
		if (cancelRovoPrompt) {
			focusEditor(editorView, { focusEditorContentArea: true });
		}
	}, [cancelRovoPrompt, editorView]);
}

function ExperienceApplicationStateWrapper(props: ExperienceApplicationStateWrapperProps) {
	const { api, editorView, endExperience } = props;
	const intl = useIntl();
	const modalRef = React.useRef<HTMLDivElement>(null);
	const [cancelRovoPrompt, setCancelRovoPrompt] = useState<() => void>();

	const positions = props.positions;
	const isEmpty = positions[0] === positions[1];
	const selectionType: SelectionType = isEmpty ? 'empty' : 'range';

	/**
	 * baseGenerate configItem is used as default configItem in command palette.
	 * Currently for every product, "freeGenarete" is used as baseGenerate.
	 * Recently, "useFreeGenerateEditorAgent" is introduced to allow
	 * 	freeGenerateEditorAgent configItem to be used as baseGenerate.
	 *  freeGenerateEditorAgent is like freeGeneerate, but it uses
	 * 	rovo agent instead of standard AI prompt.
	 *
	 * Can't set "freeGenerateEditorAgent" directly as baseGenerate in prebuilt config,
	 * 	because creating configItem requires agent to be passed in.
	 * But agents are not available at compile time or event when plugin is loaded.
	 * So we need to swap baseGenerate here.
	 */
	let configItem = props.configItem;
	let baseGenerate = props.editorPluginAIProvider.baseGenerate;
	if (props.defaultAgent && canReplaceBaseGenerate(props.editorPluginAIProvider)) {
		baseGenerate = createFreeGenerateEditorAgentConfigItem(props.defaultAgent);

		if (props.configItem.key === CONFIG_ITEM_KEYS.FREE_GENERATE) {
			configItem = baseGenerate;
		}
	}

	const { isUserScroll, isProgrammaticScroll } = useScrollDetection({ modalRef });

	const streamFlowPacerRef = useStreamFlowPacerRef();

	const analyticsFlow = useAnalyticsFlow();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	let streamingHandler: StreamingHandler | undefined;
	if (fg('platform_editor_ai_streaming_handler')) {
		streamingHandler = createStreaming({
			fireAIAnalyticsEvent,
			aiInteractionID: analyticsFlow.getLastAiInteractionId(),
			streamFlowPacer: streamFlowPacerRef.current,
		});
	}

	const { context, state, send } = useAIExperienceMachine({
		configItem,
		baseGenerate,
		intl,
		selectionType,
		streamingHandler,
		props,
		positions,
		isEmpty,
	});

	React.useEffect(() => {
		if (props.defaultAgent && canReplaceBaseGenerate(props.editorPluginAIProvider)) {
			const newBaseGenerate = createFreeGenerateEditorAgentConfigItem(props.defaultAgent);
			send({
				type: 'update baseGenerate and configItem',
				baseGenerate: newBaseGenerate,
				configItem: newBaseGenerate,
			});
		}
	}, [props.defaultAgent, props.editorPluginAIProvider, send]);

	const [start, end] = props.positions;
	React.useEffect(() => {
		// Note -- the intention is to have the state machine created at a higher
		// level, and have the positions updated without being passed into this react
		// component.
		send({ type: 'update positions', positions: [start, end] });
		// Separate start/end as React will recognise tuples recreated
		// as a change, even if the values don't change
	}, [send, start, end]);

	useFocusEditor({ modalRef, editorView, state, cancelRovoPrompt });

	React.useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		/**
		 * KeyDown (as opposed to KeyPress) event is used to handle the escape key so that
		 * other escape key handlers will not override this behaviour.
		 * Additionally this aligns with other escape listeners e.g. for closing typeahead popup.
		 */
		function handleEscapeKeyDown(event: KeyboardEvent) {
			// Exit early if the key is not Escape
			if (event.key !== 'Escape') {
				return;
			}

			// Continue only if the AI modal is open / in active state
			if (typeof state === 'string' || !('modal active state' in state)) {
				return;
			}

			// If nextSessionId exists, let the SessionUIComponent manage the escape behaviour
			if (
				api?.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState
					.nextSessionId &&
				fg('platform_editor_ai_aifc_patch_beta_2')
			) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const modalActiveState = state?.['modal active state'];
			const experience = modalActiveState?.experience;

			if (fg('aifc_create_enabled')) {
				// in command palette screen, AI modal renders RovoInlineChatPrompt component
				// and cancelRovoPrompt is from it
				if (experience === 'command palette state') {
					if (cancelRovoPrompt) {
						cancelRovoPrompt();
					} else if (fg('platform_editor_ai_aifc_patch_beta')) {
						endExperience();
					}
					return;
				}

				const isLoading =
					'loading state' in experience && experience['loading state'] === 'loading';
				const isPreview = 'review state' in experience && experience['review state'] === 'preview';

				// pressing ESC in loading or accept/reject screen should show the discard screen
				if (isLoading || isPreview) {
					// don't use `send({ type: 'escape' })` here because it will transition to `command palette state`
					if (modalActiveState?.discard === 'active') {
						send({ type: 'disable discard' });
					} else {
						send({ type: 'enable discard' });
					}
				}

				return;
			}

			if (
				typeof experience === 'object' &&
				'loading state' in experience &&
				experience['loading state'] === 'loading'
			) {
				if (modalActiveState?.discard === 'active') {
					send({ type: 'disable discard' });
				} else {
					send({ type: 'enable discard' });
				}
			} else {
				if (shouldDiscardEndExperienceImmediately(context.promptTrigger, context.responseHistory)) {
					endExperience();
					return;
				}

				send({ type: 'escape' });
			}
		}

		/**
		 * Handle KeyUp event to prevent unexpected escape behaviours when
		 * there are other escape handlers outside of component (e.g. in product)
		 */
		function handleEscapeKeyUp(event: KeyboardEvent) {
			// Exit early if the key is not Escape
			if (event.key !== 'Escape') {
				return;
			}

			// Continue only if the AI modal is open / in active state
			if (typeof state === 'string' || !('modal active state' in state)) {
				return;
			}

			// If nextSessionId exists, let the SessionUIComponent manage the escape behaviour
			if (
				api?.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState
					.nextSessionId &&
				fg('platform_editor_ai_aifc_patch_beta_2')
			) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
		}

		function handleClickOutside(event: MouseEvent) {
			if (!fg('aifc_create_enabled')) {
				return;
			}

			// Continue only if the AI modal is open / in active state
			if (typeof state === 'string' || !('modal active state' in state)) {
				return;
			}

			// If nextSessionId exists, let the SessionUIComponent manage the escape behaviour
			if (
				api?.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState
					.nextSessionId &&
				fg('platform_editor_ai_aifc_patch_beta_2')
			) {
				return;
			}

			const modalActiveState = state?.['modal active state'];
			const experience = modalActiveState?.experience;
			const isPreview =
				typeof experience === 'object' &&
				'review state' in experience &&
				experience['review state'] === 'preview';

			// click outside of the accept/reject screen should show the discard screen
			if (
				isPreview &&
				modalRef.current &&
				event.target &&
				!modalRef.current.contains(event.target as Node) &&
				modalActiveState?.discard === 'inactive'
			) {
				send({ type: 'enable discard' });
			}
		}

		// initially, editor is focused
		// when AI modal is loading and user presses ESC
		// we will show the AI modal discard screen
		// which will make AI modal to be auto focused
		// now if user presses ESC again,
		// we will return to the AI modal loading screen
		// so whenever user presses ESC, either the AI modal or the editor is focused
		// so the listener should be attached to the common ancestor of the AI Modal and the editor
		const node = fg('aifc_create_enabled')
			? editorView.dom.closest<HTMLElement>('.ak-editor-content-area')
			: modalRef.current;

		if (!node) {
			return;
		}

		return bindAll(node, [
			{ type: 'keydown', listener: handleEscapeKeyDown },
			{ type: 'keyup', listener: handleEscapeKeyUp },
			{ type: 'click', listener: handleClickOutside },
		]);
	}, [
		modalRef,
		state,
		send,
		context.promptTrigger,
		context.responseHistory,
		endExperience,
		editorView,
		cancelRovoPrompt,
		api,
	]);

	const [isMounted, setIsMounted] = React.useState(false);
	React.useEffect(() => {
		if (!fg('platform_editor_ai_scroll_into_view_fix')) {
			return;
		}
		if (!modalRef.current) {
			return;
		}

		startTransition(() => {
			setIsMounted(true);
		});
	}, [modalRef]);

	React.useEffect(() => {
		if (
			!fg('platform_editor_ai_scroll_into_view_fix') ||
			/**
			 * TODO EDITOR-1181:
			 * Modal will always be in viewport so later on we should not need to scroll into view.
			 */
			fg('aifc_create_enabled')
		) {
			return;
		}
		if (!modalRef.current) {
			return;
		}

		if (isMounted) {
			modalRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
		}
	}, [isMounted, modalRef]);

	const aiExperienceCommonData = React.useMemo(
		() => ({
			appearance: props.appearance,
			// configItem can be changed within state machine, so always get it from machine context.
			configItem: context.configItem,
			editorPluginAIProvider: props.editorPluginAIProvider,
			endExperience: props.endExperience,
			lastTriggeredFrom: props.lastTriggeredFrom,
			providerFactory: props.providerFactory,
			editorView: props.editorView,
			positions: props.positions,
			triggerMethod: props.triggerMethod,
			triggeredFor: props.triggeredFor,
			sendToAIExperienceMachine: send,
			modalRef,
			isUserScroll,
			isProgrammaticScroll,
			engagementPlatformApi: props.engagementPlatformApi,
			includeKnowledgeFromCurrentPage: context.usePageKnowledge,
			canInlineStreamChanges: props.canInlineStreamChanges,
			hideLoadingScreen: props.hideLoadingScreen,
			setCancelRovoPrompt,
			aiSessionId: props.interactionSessionId,
		}),
		[
			context.configItem,
			context.usePageKnowledge,
			props.editorView,
			props.editorPluginAIProvider,
			props.appearance,
			props.endExperience,
			props.lastTriggeredFrom,
			props.providerFactory,
			props.positions,
			props.triggerMethod,
			props.triggeredFor,
			props.engagementPlatformApi,
			props.canInlineStreamChanges,
			send,
			modalRef,
			isUserScroll,
			isProgrammaticScroll,
			props.hideLoadingScreen,
			props.interactionSessionId,
		],
	);

	React.useEffect(() => {
		analyticsFlow.updateCommonAttributes({ traceIds: context.traceIds });
	}, [analyticsFlow, context.traceIds]);

	return (
		<div
			// We set these to ensure that when focus initially moves to
			// an element within the modal, that context about the modal
			// (e.g. "Atlassian Intelligence Dialog") is announced too
			role="dialog"
			// ED-19787
			// We would like to use aria-labelledBy so we can label this
			// dynamically for each screen, however it's not working as expected
			// in Chrome and Firefox (works in Safari). For now we'll use the dialog
			// role and make the first element of each screen a screen-reader accessible
			// heading for the experience.
			aria-label={intl.formatMessage(aiExperienceApplicationMessages.dialogAriaLabel)}
			ref={modalRef}
		>
			<AIExperienceCommonDataContext.Provider value={aiExperienceCommonData}>
				<ExperienceApplicationStateManager
					api={api}
					state={state}
					context={context}
					initialPrompt={props.initialPrompt}
					modalRef={modalRef}
					streamFlowPacer={streamFlowPacerRef.current}
				/>
			</AIExperienceCommonDataContext.Provider>
		</div>
	);
}

export function ExperienceApplication(props: ExperienceApplicationProps) {
	const { api, editorView, canInlineStreamChanges } = props;

	const analyticsFlow = useCreateAnalyticsFlow({
		invokeAttributes: {
			invokedFrom: props.lastTriggeredFrom,
			invokedFor: props.triggeredFor ? props.triggeredFor.name : 'text',
			triggerMethod: props.triggerMethod,
			experienceName: props.configItem.key,
			featureId: props.configItem.featureId,
			aiGroupId: props.aiGroupId,
			aiSessionId: props.interactionSessionId,
		},
		canInlineStreamChanges: props.canInlineStreamChanges,
	});

	// initialPositions is updated many times during in-doc streaming,
	// but we only want to update the common analytics attributes once,
	// so we check if they've already been updated
	const commonAnalyticsAttributesUpdatedRef = useRef(false);
	React.useEffect(() => {
		if (fg('aifc_create_enabled')) {
			if (commonAnalyticsAttributesUpdatedRef.current) {
				return;
			}
			commonAnalyticsAttributesUpdatedRef.current = true;
		}

		const isInvokedFromEmptyPage = isEmptyDocument(props.editorView.state.doc);
		analyticsFlow.updateCommonAttributes({
			isInvokedFromEmptyPage,
			/**
			 * Confidence score will be used passed in analytics events.
			 * It is currently only set when "Improve writing" is triggered from the
			 * 	"Improve writing" nudge.
			 */
			nudgeMetrics: props.nudgeMetrics,
		});
		if (props.initialPositions) {
			const selectionCategory = getSelectionCategory(props.editorView, props.initialPositions);

			if (fg('platform_editor_ai_aifc_analytics')) {
				const { charCount, wordCount } = rangeCharWordCount(
					props.editorView.state.doc,
					props.initialPositions[0],
					props.initialPositions[1],
				);

				analyticsFlow.updateCommonAttributes({
					selectionCharCount: charCount,
					selectionWordCount: wordCount,
				});
			}

			analyticsFlow.updateCommonAttributes({ isInvokedFromEmptyPage, selectionCategory });
		}
	}, [analyticsFlow, props.editorView, props.initialPositions, props.nudgeMetrics]);

	// finish analytics flow and fire remaining steps on unmount
	React.useEffect(() => {
		return () => {
			// have to postpone until animation frame to make sure we end analytics flow after child components unmount
			// which is where we enqueue last events
			window.requestAnimationFrame(() => {
				analyticsFlow.fireQueuedStep();
			});
		};
	}, [analyticsFlow]);

	// This makes sure that the selection preview is hidden when the experience is unmounted
	React.useEffect(() => {
		return () => {
			const tr = props.editorView.state.tr;
			tr.setMeta('hideSelectionPreview', true);
			props.editorView.dispatch(tr);
		};
	}, [props.editorView]);

	// focus editor when the AI modal is unmounted
	useEffect(() => {
		return () => {
			if (fg('aifc_create_enabled')) {
				focusEditor(editorView);
				// when we set the selection with AI modal open,
				// prosemirror won't update the selection because editor is not focused
				// and when the selection is empty in a paragraph,
				// the cursor may be (randomly) placed somewhere else within the paragraph when we focus the editor
				// so we need to re-set the selection after we focus the editor to make prosemirror update the selection correctly
				// and we have to set the selection to somewhere else first
				// because prosemirror will skip the selection update if the selection doesn't change
				// https://hello.jira.atlassian.cloud/browse/EDITOR-1269
				if (
					editorView.state.selection instanceof TextSelection &&
					editorView.state.selection.empty &&
					editorView.state.selection.$to.parent.isTextblock &&
					editorView.state.selection.$to.parent.childCount > 0
				) {
					const to = editorView.state.selection.to;

					let tr = editorView.state.tr;
					tr.setSelection(TextSelection.create(tr.doc, 0));
					tr.setMeta('addToHistory', false).setMeta('scrollIntoView', false);
					editorView.dispatch(tr);

					tr = editorView.state.tr;
					tr.setSelection(TextSelection.create(tr.doc, to));
					tr.setMeta('addToHistory', false).setMeta('scrollIntoView', false);
					editorView.dispatch(tr);
				}
			}
		};
	}, [editorView]);

	// turn diff off when unmount
	useEffect(() => {
		if (fg('aifc_create_enabled')) {
			return () => {
				const autoAcceptAIChanges = canInlineStreamChanges();
				if (
					!autoAcceptAIChanges &&
					api?.trackChanges?.sharedState.currentState()?.isDisplayingChanges
				) {
					api?.core.actions.execute(api?.trackChanges?.commands.toggleChanges);
				}
			};
		}
	}, [api, canInlineStreamChanges]);

	return (
		<ModalRegionErrorBoundary
			onCloseFallback={() => {
				props.endExperience();
			}}
		>
			<AnalyticsFlowContextProvider value={analyticsFlow}>
				<UFOSegment name="EditorAICommandPalette">
					<ExperienceApplicationStateWrapper
						// Ignored via go/ees005
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...props}
						analyticsContext={{
							aiSessionId: analyticsFlow.aiSessionId,
							aiGroupId: props.aiGroupId ?? analyticsFlow.aiSessionId,
						}}
					/>
				</UFOSegment>
			</AnalyticsFlowContextProvider>
		</ModalRegionErrorBoundary>
	);
}
